import React, { useState, useEffect, useContext } from "react";
import { Box, Button, CircularProgress, Container, Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { I18nContext } from "../../I18nProvider";
import { Proyectos } from "./Proyecto";
import IresearhService from "../../services/IresearchService";
import { ProyectoOds } from "../../Interface/IProyectoOds";
import { PublicacionActual } from "../../Interface/IPublicacion";
import { Publicaciones } from "./Publicaciones";
import SweetAlertUtil from "../../utils/SweetAlert";
import { NotFoundPage } from "../Error/ErrorNotFound";
import { themePalette } from "../../config/theme.config";
import { TbSearch, TbX } from "react-icons/tb";



const odsMap: Record<string, number> = {
    "poverty": 1,                   "pobreza": 1,
    "hunger": 2,                    "hambre": 2,
    "health": 3,                    "salud": 3,
    "education": 4,                 "educacion": 4,
    "gender-equality": 5,           "genero": 5,
    "water-and-sanitation": 6,      "agua": 6,
    "energy": 7,                    "energia": 7,
    "economic-growth": 8,           "crecimiento-economico": 8,
    "infrastructure": 9,            "infraestructura": 9,
    "inequality": 10,               "desigualdad": 10,
    "cities": 11,                   "ciudades": 11,
    "climate-change": 13,           "cambio-climatico": 13,
    "oceans": 14,                   "oceanos": 14,
    "biodiversity": 15,             "biodiverssidad": 15,
    "peace-justice": 16,            "paz-justicia": 16,
    "partnerships": 17,             "asociaciones": 17, 
    "sustainable-consumption-production": 12,
    "sostenibilidad": 12,
};

const componentsMap: Record<"publicaciones" | "proyectos", React.FC<any>> = {
    publicaciones: Publicaciones,
    proyectos: Proyectos,
};

export const Ods: React.FC = () => {
    const { id } = useParams<{ id?: string }>();

    
    const numericValue = parseInt(id ?? "", 10);
    let computedOdsNumber: number | undefined;
    if (!isNaN(numericValue) && numericValue >= 1 && numericValue <= 17) {
        computedOdsNumber = numericValue;
    } else {
        const paramId = (id ?? "").toLowerCase();
        computedOdsNumber = odsMap[paramId];
    }

    
    const [activeComponent, setActiveComponent] = useState<"publicaciones" | "proyectos">("publicaciones");
    const [totalPub, setTotalPub] = useState<string>();
    const [totalCit, setTotalCit] = useState<string>();
    const [loadingDatos, setLoadingDatos] = useState<boolean>(true);
    const [proyectos, setProyectos] = useState<ProyectoOds[]>([]);
    const [loadingProy, setLoadingProy] = useState<boolean>(true);
    const [publicaciones, setPublicaiones] = useState<PublicacionActual[]>([]);
    const [loadingPub, setLoadingPub] = useState<boolean>(true);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [page, setPage] = useState<number>(1);

    const [searchTerm, setSearchTerm] = useState<string>(""); 
    const [error, setError] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [proyectosFil, setProyectosFil] = useState<ProyectoOds[]>([]);
    const [searchTermProy, setSearchTermProy] = useState<string>(""); 
    const [errorProy, setErrorProy] = useState<boolean>(false);


    const context = useContext(I18nContext);
    if (context === null) {
        throw new Error("El contexto I18n no está inicializado.");
    }

    const handleSearch = () => {
        if (searchTerm.length >= 4 && searchTerm.length <= 100) {
            setSearchQuery(searchTerm); 
            setError(false);
        } else {
            setError(true);
        }
    };

    const handleReset = () => {
        setSearchTerm("")
        setSearchQuery("")

    }


    const normalizeText = (text: string) =>
        text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    const handleSearchProy = () => {
        if (searchTermProy.length < 4 || searchTermProy.length > 100) {
            setErrorProy(true);
            return;
        }
        setErrorProy(false);
        const filtered = proyectos.filter((proyecto) =>
            normalizeText(proyecto.titulo).includes(normalizeText(searchTermProy))
        );
        setProyectosFil(filtered);
    };

    const handleResetProy = () => {
        setSearchTermProy("");
        setProyectosFil(proyectos);
        setErrorProy(false);
    };


    useEffect(() => {
        if (id && computedOdsNumber) {
            IresearhService.GetProyectosByOds(computedOdsNumber.toString())
                .then((proy) => {
                    if (proy.status.isSuccessfully) {
                        setProyectos(proy.listData);
                        setProyectosFil(proy.listData);
                    } else {
                        SweetAlertUtil.alertaAdvertenciaList("Error en ODS", proy.status.messages);
                    }
                })
                .catch((error) => console.error("Error fetching projects:", error))
                .finally(() => {
                    setLoadingProy(false);
                });

            IresearhService.GetByNpublicacionByOds(computedOdsNumber.toString())
                .then((datosOds) => {
                    setTotalPub(datosOds.data.publications);
                    setTotalCit(datosOds.data.citations);
                })
                .catch((error) => console.error("Error fetching data:", error))
                .finally(() => {
                    setLoadingDatos(false);
                });
        }
    }, [id, computedOdsNumber]);

    useEffect(() => {
        if (id && computedOdsNumber) {
            setLoadingPub(true);
            IresearhService.GetPublicationsByOds(computedOdsNumber.toString(), page, searchQuery)
                .then((pub) => {
                    setTotalRows(pub.data.totalCount);
                    setPublicaiones(pub.data.data);
                })
                .catch((error) => console.error("Error fetching publications:", error))
                .finally(() => {
                    setLoadingPub(false);
                });
        }
    }, [id, page, computedOdsNumber, searchQuery]); 

    
    if (!computedOdsNumber) {
        return (
            
                <NotFoundPage />
            
        );
    }

    
    const imagePath = require(`../../assets/img/ods/ods${computedOdsNumber}.jpg`);

    return (
        <Container style={{ padding: "20px" }}>
            <Grid container direction="row" spacing={2} sx={{ borderBottom: `2px solid ${context.t.translate("theme.NAVCOLOR")}` }}>
                <Grid item xs={12} sm={3} md={2} sx={{ padding: "20px" }}>
                    <Box display="flex" justifyContent="center">
                        <img src={imagePath} alt="ODS" style={{ maxWidth: "100%", height: "auto", maxHeight: "150px" }} />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    <h3>
                        {context.t.translate("home.ods")} # {computedOdsNumber}
                    </h3>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    {loadingDatos ? (
                        <CircularProgress />
                    ) : (
                        <Box>
                            <h3>{context.t.translate("ods.datos")}</h3>
                            <h5>
                                {context.t.translate("ods.publicaciones")}: {totalPub}
                            </h5>
                        </Box>
                    )}
                </Grid>
            </Grid>

            <Grid container justifyContent="center" alignItems="center" spacing={2} sx={{ padding: "20px" }}>
                <Grid item xs={12} sm={2} md={2}>
                    <Button fullWidth onClick={() => setActiveComponent("publicaciones")} variant="outlined"
                        sx={{
                            color: activeComponent === "publicaciones" ? "white !important" : themePalette.NAVCOLOR,
                            backgroundColor: activeComponent === "publicaciones" ? themePalette.NAVCOLOR : "transparent",
                            borderColor: themePalette.NAVCOLOR,
                            '&:hover': {
                                backgroundColor: activeComponent === "publicaciones" ? themePalette.NAVCOLOR : "rgba(0, 0, 0, 0.1)",
                            },
                            height: { xs: "auto", md: "60px", },
                        }}
                    >{context.t.translate("investigador.publicaciones")}</Button>
                </Grid>
                <Grid item xs={12} sm={2} md={2}>
                    <Button fullWidth onClick={() => setActiveComponent("proyectos")} variant="outlined"
                        sx={{
                            color: activeComponent === "proyectos" ? "white !important" : themePalette.NAVCOLOR,
                            backgroundColor: activeComponent === "proyectos" ? themePalette.NAVCOLOR : "transparent",
                            borderColor: themePalette.NAVCOLOR,
                            '&:hover': {
                                backgroundColor: activeComponent === "proyectos" ? themePalette.NAVCOLOR : "rgba(0, 0, 0, 0.1)",
                            },
                            height: { xs: "auto", md: "60px", },
                        }}
                    >{context.t.translate("investigador.proyectos")}</Button>
                </Grid>
            </Grid>

            <Box sx={{ marginTop: "20px" }}>
                {activeComponent === "publicaciones" ? (
                    loadingPub ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                            <Grid container justifyContent="center" alignItems="center" spacing={2} >

                                <Grid item xs={12} sm={7} md={7} sx={{marginBottom: "10px"} }>
                                    <TextField
                                        label="Búsqueda de Publicaciones"
                                        variant="outlined"
                                        fullWidth
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearch();
                                            }
                                        }}
                                        error={error}
                                        helperText={error ? "El texto debe tener de 4 a 100 caracteres" : ""}
                                        InputProps={{
                                            endAdornment: (

                                                <InputAdornment position="end">

                                                    {searchTerm && (
                                                        <IconButton
                                                            sx={{ color: themePalette.COLOR_SIDEBAR_ICON }}
                                                            onClick={handleReset}
                                                        >
                                                            <TbX />
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        sx={{ color: themePalette.COLOR_SIDEBAR_ICON }}
                                                        onClick={handleSearch}
                                                    >
                                                        <TbSearch />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                            </Grid>



                        <componentsMap.publicaciones
                            publicaciones={publicaciones}
                            totalRows={totalRows}
                            page={page}
                            setPage={setPage}
                        />
                        </Grid>
                    )
                ) : activeComponent === "proyectos" ? (
                    loadingProy ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                            <CircularProgress />
                        </Box>
                        ) : (
                                <Grid container justifyContent="center" alignItems="center" spacing={2} >

                                    <Grid item xs={12} sm={7} md={7} sx={{ marginBottom: "10px" }}>

                                            <TextField
                                                label="Búsqueda de Proyectos"
                                                variant="outlined"
                                                fullWidth
                                                value={searchTermProy}
                                                onChange={(e) => setSearchTermProy(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        handleSearchProy();
                                                    }
                                                }}
                                                error={errorProy}
                                                helperText={errorProy ? "El texto debe tener de 4 a 100 caracteres" : ""}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {searchTermProy && (
                                                                <IconButton
                                                                    sx={{ color: themePalette.COLOR_SIDEBAR_ICON }}
                                                                    onClick={handleResetProy}
                                                                >
                                                                    <TbX />
                                                                </IconButton>
                                                            )}
                                                            <IconButton
                                                                sx={{ color: themePalette.COLOR_SIDEBAR_ICON }}
                                                                onClick={handleSearchProy}
                                                            >
                                                                <TbSearch />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                        />
                                    </Grid>


                                    <componentsMap.proyectos proyectos={proyectosFil} />
                          </Grid>
                    )
                ) : null}
            </Box>
        </Container>
    );
};
